import { ArrowRight, AlertTriangle, CheckCircle2, ExternalLink, Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useCitAccrualForPeriod } from '../../../hooks/useCitAccruals'
import { endOfMonthIso } from '../../../lib/citComputation'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const NOW = new Date()
const CURRENT_YEAR = NOW.getFullYear()
const YEAR_OPTIONS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1]

interface Props {
  year: number
  month: number
  onYearChange: (year: number) => void
  onMonthChange: (month: number) => void
  onContinue: () => void
}

export function CitPeriodPicker({
  year, month, onYearChange, onMonthChange, onContinue,
}: Props) {
  const periodEndIso = endOfMonthIso(year, month)
  const { data: existing, isLoading } = useCitAccrualForPeriod(periodEndIso)

  const monthLabel = `${MONTH_NAMES[month - 1]} ${year}`
  const blocked = !!existing
  const canContinue = !blocked && !isLoading

  return (
    <div className="max-w-2xl mx-auto space-y-6 py-4 sm:py-8">
      <header>
        <h1 className="text-[22px] font-medium text-gray-900">CIT Accrual</h1>
        <p className="text-sm text-slate-500 mt-1">
          Compute and post Company Income Tax (CIT) plus Education Tax (TET)
          for one period. Posting is atomic and immutable.
        </p>
      </header>

      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-6 space-y-5">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Pick period</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            CIT is computed cumulatively from January through this month-end.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Year</label>
            <select
              className="input"
              value={year}
              onChange={e => onYearChange(parseInt(e.target.value, 10))}
            >
              {YEAR_OPTIONS.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Month</label>
            <select
              className="input"
              value={month}
              onChange={e => onMonthChange(parseInt(e.target.value, 10))}
            >
              {MONTH_NAMES.map((name, i) => (
                <option key={name} value={i + 1}>{name}</option>
              ))}
            </select>
          </div>
        </div>

        <StatusCheck
          loading={isLoading}
          blocked={blocked}
          monthLabel={monthLabel}
          journalEntryNumber={existing?.journalEntryNumber ?? null}
        />

        <div className="flex justify-end pt-2">
          <button
            type="button"
            disabled={!canContinue}
            onClick={onContinue}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Compute Preview
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    </div>
  )
}

interface StatusProps {
  loading: boolean
  blocked: boolean
  monthLabel: string
  journalEntryNumber: string | null
}

function StatusCheck({ loading, blocked, monthLabel, journalEntryNumber }: StatusProps) {
  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-center gap-3">
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <p className="text-sm text-slate-600">Checking period&hellip;</p>
      </div>
    )
  }

  if (blocked) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-red-800">
              CIT already accrued for {monthLabel}.
            </p>
            <p className="text-xs text-red-700">
              Each period can only be accrued once. Corrections happen in the
              next period&rsquo;s accrual.
            </p>
            {journalEntryNumber && (
              <Link
                to={`/accounting/journal?search=${encodeURIComponent(journalEntryNumber)}`}
                className="text-xs font-medium text-red-700 underline hover:text-red-900 inline-flex items-center gap-1 mt-1"
              >
                View journal entry {journalEntryNumber}
                <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-sage-200 bg-sage-50 p-4">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="h-5 w-5 text-sage-600 flex-shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-sage-800">
            Ready to compute CIT for {monthLabel}.
          </p>
          <p className="text-xs text-sage-700">
            Continue to see year-to-date profit, classification, and the
            tax charge before posting.
          </p>
        </div>
      </div>
    </div>
  )
}
